import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FileText, Send, CheckCircle, AlertTriangle, Eye,
    ChevronLeft, ChevronRight, X, DollarSign, TrendingUp,
} from 'lucide-react';

function InvoiceManagement() {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [meta, setMeta] = React.useState<any>(null);
    const [page, setPage] = React.useState<number>(1);
    const [statusFilter, setStatusFilter] = React.useState<string>('');

    // Detail modal
    const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
    const [detailLoading, setDetailLoading] = React.useState<boolean>(false);

    // Status update modal
    const [showStatusModal, setShowStatusModal] = React.useState<any>(null);
    const [statusAction, setStatusAction] = React.useState<string>('');
    const [paymentRef, setPaymentRef] = React.useState<string>('');
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    React.useEffect(() => { loadInvoices(); }, [page, statusFilter]);

    const loadInvoices = () => {
        setLoading(true);
        ApiGatewayApis.getAllInvoices(page, { status: statusFilter })
            .then((response: any) => {
                setLoading(false);
                if (response?.data) {
                    setInvoices(response.data.data || []);
                    setMeta(response.data.meta || null);
                }
            }).catch(() => setLoading(false));
    };

    const viewDetail = (id: any) => {
        setDetailLoading(true);
        ApiGatewayApis.getInvoiceDetail(id).then((response: any) => {
            setDetailLoading(false);
            if (response?.data?.data) {
                setSelectedInvoice(response.data.data);
            }
        }).catch(() => setDetailLoading(false));
    };

    const updateStatus = () => {
        if (!showStatusModal || !statusAction) return;
        setActionLoader(true);
        const data: any = { status: statusAction };
        if (statusAction === 'paid' && paymentRef) {
            data.payment_reference = paymentRef;
        }
        ApiGatewayApis.updateInvoiceStatus(showStatusModal.id, data)
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success(`Invoice marked as ${statusAction}`);
                    setShowStatusModal(null);
                    setStatusAction('');
                    setPaymentRef('');
                    loadInvoices();
                }
            }).catch(() => {
                setActionLoader(false);
                toast.error('Failed to update invoice status');
            });
    };

    const getStatusBadge = (status: string) => {
        const styles: any = {
            draft: 'bg-gray-100 text-gray-600',
            sent: 'bg-blue-100 text-blue-700',
            viewed: 'bg-purple-100 text-purple-700',
            paid: 'bg-green-100 text-green-700',
            overdue: 'bg-red-100 text-red-700',
            cancelled: 'bg-gray-100 text-gray-500',
        };
        return (
            <span className={`px-2 py-1 rounded-full text-[11px] font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
                {status?.toUpperCase()}
            </span>
        );
    };

    return (
        <>
            <AdminSidebar title="Invoice Management" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Partner Invoices" />
                <ToastContainer autoClose={3000} />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    {/* Filters */}
                    <div className="flex gap-2 lg:pt-5 pt-3 flex-wrap">
                        {['', 'draft', 'sent', 'viewed', 'paid', 'overdue'].map((s) => (
                            <button key={s}
                                onClick={() => { setStatusFilter(s); setPage(1); }}
                                className={`px-3 py-1.5 rounded-full text-[12px] font-[500] border transition-all ${statusFilter === s
                                    ? 'bg-[#E04343] text-white border-[#E04343]'
                                    : 'bg-white text-[#808080] border-[#E2E8F0] hover:border-[#E04343]'
                                    }`}>
                                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="px-6 mt-4 bg-white pt-[24px] rounded-[10px] pb-10">
                        <div className="flex justify-between items-center mb-4">
                            <span className="font-[700]">All Invoices</span>
                        </div>
                        <div className="relative overflow-x-auto">
                            {loading ? (
                                <div className="flex justify-center py-16">
                                    <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                                </div>
                            ) : invoices.length === 0 ? (
                                <div className="text-center py-16 text-[#999]">
                                    <FileText size={40} className="mx-auto mb-3 text-[#ddd]" />
                                    <p>No invoices found</p>
                                </div>
                            ) : (
                                <table className="w-full text-sm text-left text-gray-500">
                                    <thead className="text-[12px] text-[#808080] bg-[#FAFAFA]">
                                        <tr>
                                            <th className="px-2 py-2">Invoice #</th>
                                            <th className="px-2 py-2">Partner</th>
                                            <th className="px-2 py-2">Period</th>
                                            <th className="px-2 py-2">Shipments</th>
                                            <th className="px-2 py-2">Total</th>
                                            <th className="px-2 py-2">NIPOST&nbsp;Cost</th>
                                            <th className="px-2 py-2">Margin</th>
                                            <th className="px-2 py-2">Status</th>
                                            <th className="px-2 py-2">Due</th>
                                            <th className="px-2 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv: any) => {
                                            const margin = Number(inv.subtotal || 0) - Number(inv.nipost_cost || 0);
                                            return (
                                                <tr key={inv.id} className="bg-white border-b text-[13px] hover:bg-[#FAFAFA]">
                                                    <td className="px-2 py-3 font-mono text-[12px] font-[600]">{inv.invoice_number}</td>
                                                    <td className="px-2 py-3">{inv.partner?.company_name || '-'}</td>
                                                    <td className="px-2 py-3 text-[12px]">{inv.period_start}<br />{inv.period_end}</td>
                                                    <td className="px-2 py-3">{inv.total_shipments}</td>
                                                    <td className="px-2 py-3 font-[600]">₦{Number(inv.total_amount || 0).toLocaleString()}</td>
                                                    <td className="px-2 py-3 text-[#999]">₦{Number(inv.nipost_cost || 0).toLocaleString()}</td>
                                                    <td className={`px-2 py-3 font-[600] ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        ₦{margin.toLocaleString()}
                                                    </td>
                                                    <td className="px-2 py-3">{getStatusBadge(inv.status)}</td>
                                                    <td className="px-2 py-3 text-[12px]">{inv.due_date}</td>
                                                    <td className="px-2 py-3">
                                                        <div className="flex gap-1">
                                                            <button onClick={() => viewDetail(inv.id)} title="View"
                                                                className="p-1 text-[#E04343] hover:bg-[#FFF0F0] rounded">
                                                                <Eye size={14} />
                                                            </button>
                                                            {inv.status === 'draft' && (
                                                                <button onClick={() => { setShowStatusModal(inv); setStatusAction('sent'); }}
                                                                    title="Mark Sent" className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                                                                    <Send size={14} />
                                                                </button>
                                                            )}
                                                            {['sent', 'viewed', 'overdue'].includes(inv.status) && (
                                                                <button onClick={() => { setShowStatusModal(inv); setStatusAction('paid'); }}
                                                                    title="Mark Paid" className="p-1 text-green-600 hover:bg-green-50 rounded">
                                                                    <CheckCircle size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination */}
                        {meta && meta.last_page > 1 && (
                            <div className="flex justify-center items-center gap-4 mt-6">
                                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-[13px] text-[#808080]">Page {page} of {meta.last_page}</span>
                                <button onClick={() => setPage(Math.min(meta.last_page, page + 1))} disabled={page >= meta.last_page}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Status Update Modal */}
                    {showStatusModal && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowStatusModal(null)}>
                            <div className="bg-white rounded-[10px] w-[400px] p-6" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-[700] text-[16px]">
                                        {statusAction === 'sent' ? 'Mark as Sent' : statusAction === 'paid' ? 'Mark as Paid' : 'Cancel Invoice'}
                                    </h3>
                                    <button onClick={() => setShowStatusModal(null)}><X size={18} /></button>
                                </div>
                                <p className="text-[13px] text-[#808080] mb-4">
                                    Invoice: <strong>{showStatusModal.invoice_number}</strong><br />
                                    Total: <strong>₦{Number(showStatusModal.total_amount || 0).toLocaleString()}</strong>
                                </p>

                                {statusAction === 'paid' && (
                                    <div className="mb-4">
                                        <label className="block text-[13px] text-[#808080] mb-1">Payment Reference (optional)</label>
                                        <input type="text" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)}
                                            placeholder="e.g. TRF-2025-001234"
                                            className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                    </div>
                                )}

                                <div className="flex gap-3 justify-end">
                                    <button onClick={() => setShowStatusModal(null)}
                                        className="px-4 py-2 text-[13px] text-[#808080] border border-[#E2E8F0] rounded-[8px] hover:bg-[#FAFAFA]">
                                        Cancel
                                    </button>
                                    <button onClick={updateStatus} disabled={actionLoader}
                                        className="px-4 py-2 text-[13px] bg-[#E04343] text-white rounded-[8px] hover:bg-[#c93636] disabled:opacity-50 flex items-center gap-2">
                                        {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}

export default InvoiceManagement;